import { taskReadPath } from "./platform-tasks.mjs";
import { COUNTERPARTY_ADMIN_SOLUTION_ID } from "./ready-solution-admin.mjs";
import { COUNTERPARTY_SOLUTION_NAME, taskSolutionVersion } from "./solution-version.mjs";

export const READY_SOLUTION_STATUS_LEGEND = [
  ["ready", "Готово"], ["off", "Выключено"], ["error", "Ошибка"], ["warning", "Есть замечания"],
];

export function counterpartyPublication(state = {}) {
  return {
    solutionId: COUNTERPARTY_ADMIN_SOLUTION_ID,
    name: COUNTERPARTY_SOLUTION_NAME,
    description: "Исследование компании, дополнительная проверка сведений и сводный черновик справки.",
    enabled: state.settings?.enabled,
    ready: Boolean(state.settings?.published),
    version: state.settings?.published?.version || "",
    loading: !state.settings && !state.error,
    loadError: !state.settings ? state.error : null,
  };
}

export function latestTaskMatchesPublication(item, task) {
  if (!task) return false;
  if (item.solutionId === COUNTERPARTY_ADMIN_SOLUTION_ID && item.version) {
    const version = taskSolutionVersion(task) || task.buildVersionRef?.version;
    return !version || version === item.version;
  }
  if (item.modelProfileVersion && task.modelProfileVersion)
    return item.modelProfileVersion === task.modelProfileVersion;
  return true;
}

// A web draft is always partial and carries the expected fact-checking caveat.
// Only execution limitations, empty output or extra warnings affect health.
export function researchResultHasIssues(result) {
  const dossier = result?.dossier || result?.data?.dossier;
  if (!dossier) return false;
  const research = [dossier, ...[dossier.parallel,dossier.independent,dossier.analysis,dossier.factCheck].filter(Boolean)];
  return research.some((draft) => draft.stopReason || draft.status === "no_data" ||
    (draft.researcherRole === "factchecker_4" && !draft.verification?.agent4Completed) ||
    (draft.artifactKind === "counterparty-researcher-1-web-draft"
      ? (draft.warnings || []).some((warning) => warning !== "Замечания Агента 4 по явным ошибкам. Проверка не подтверждает все сведения справки." && !/^Черновое исследование Агента (1|1\.1|2|3)\. Сведения и соответствие компании требуют последующей проверки\.$/.test(warning))
      : draft.status === "partial"));
}

export async function readLatestSolutionRun(api, solutionId) {
  const page = await api.request(`/v1/platform/tasks?${new URLSearchParams({ solutionId, limit: "1" })}`);
  const task = page.items?.[0] || null;
  if (task?.status !== "completed") return { task };
  try {
    const result = await api.request(taskReadPath(task.taskId, task.scope, "/result/content"));
    return { task, hasIssues: researchResultHasIssues(result) };
  } catch {
    return { task, resultUnavailable: true };
  }
}

export function readySolutionStatus(item, execution = {}) {
  if (item.loadError) return { tone: "error", label: "Не удалось проверить", detail: item.loadError };
  if (item.loading) return { tone: "off", label: "Проверяем…", detail: "Загружаем настройки решения." };
  if (!item.enabled) return { tone: "off", label: "Выключено", detail: item.message || "Запуски решения отключены." };
  if (!item.ready) return { tone: "error", label: "Не готово к запуску", detail: item.message || "Нет опубликованной совместимой версии." };
  if (execution.error) return { tone: "warning", label: "Не удалось проверить запуски", detail: execution.error };
  if (execution.loading) return { tone: "off", label: "Проверяем…", detail: "Загружаем последний запуск." };
  const current = latestTaskMatchesPublication(item, execution.task);
  if (current && ["failed", "timed_out"].includes(execution.task.status))
    return { tone: "error", label: "Ошибка последнего запуска", detail: execution.task.statusReason?.message || "Последний запуск завершился с ошибкой." };
  if (current && (execution.hasIssues || execution.resultUnavailable || ["cancelled", "partial", "awaiting_input"].includes(execution.task.status)))
    return { tone: "warning", label: "Есть замечания", detail: execution.resultUnavailable ? "Последний запуск завершён, но результат не удалось проверить." : execution.task.statusReason?.message || "Последний запуск завершён с ограничениями или требует внимания." };
  return { tone: "ready", label: "Готово", detail: !execution.task || !current ? "Версия опубликована и включена. Запусков этой версии пока нет." : "Версия опубликована и включена." };
}

export function latestRunLabel(task) {
  return task ? ({ completed: "Готово", failed: "Ошибка", timed_out: "Истекло время", cancelled: "Отменено", running: "Выполняется", queued: "В очереди", awaiting_input: "Ожидает данных", partial: "С замечаниями" }[task.status] || "Статус неизвестен") : "Запусков пока нет";
}
